import { get } from "micro-dash";
import React from "react";

import blockTypes from "../../../constants/blockTypes";
import {
    getBlockHeaderClassName,
    getBlockTitle,
} from "../../../helpers/blockHelpers";
import formatMediaDuration from "../../../helpers/formatMediaDuration";
import formatFilename from "../../../helpers/formatFilename";
import ShowIf from "../../ShowIf";
import DroppableBlock from "../DroppableBlock";

import styles from "./Block.module.scss";
import i18n from "../../../constants/i18n";
import { SinglePhoto } from "./SinglePhoto";
import { MultiplePhotos } from "./MultiplePhoto";
import { DoublePhoto } from "./DoublePhoto";
import { SingleVideo } from "./SingleVideo";

const ExtraHeaderPlaceholder = () => (
    <>
        <span className="icon">
            <i className="fas fa-music"></i>
        </span>
        <span className="icon is-small">
            <i className="fas fa-plus"></i>
        </span>
        <span className="icon">
            <i className="fas fa-video"></i>
        </span>
        <span className="icon is-small">
            <i className="fas fa-plus"></i>
        </span>
    </>
);

const shouldShowExtraHeader = (block) =>
    block.isWalkInBlock ||
    block.isWalkOutBlock ||
    (block.typeMutable && !hasAssignedAsset(block));

const hasAssignedAsset = (block) => {
    return (
        (block.type === blockTypes.VIDEO && block.video) ||
        (block.type === blockTypes.AUDIO && block.audioTrack)
    );
};

const BlockView = ({
    block,
    index,
    onEditButtonClick,
    onChangeImageControlClick,
    onWalkInOrOutBlockMediaPlaceholderClick,
    speechPhotoLimit,
    alternativePlaceholderImage,
    showBlockPhotoUploadModal,
    showBlockPhotoProcessingFailureModal,
    room,
}) => {
    const alternativePlaceholderUrl = alternativePlaceholderImage
        ? alternativePlaceholderImage.fileName
        : null;
    const allowPhotoUpload =
        block.type === blockTypes.SLIDESHOW ||
        block.imageIds.length < speechPhotoLimit;
    const failedPhotos = block.photos.filter(
        (photo) => photo?.processingFailure
    );
    const failedPhotoTitle = failedPhotos
        .map((photo) =>
            i18n.generic.processingFailureMessage.replace(
                "<FILENAME>",
                get(photo, ["originalFileName"], "")
            )
        )
        .join("\n");

    return (
        <div
            className={`${styles.container} block ${
                block.isWalkInBlock ? "tutorial-step-4" : ""
            }`}
            key={block.id}
        >
            <div className={styles.blockIndicatorContainer}>
                {" "}
                <div className={styles.blockIndex}>{index + 1}</div>
                <ShowIf
                    condition={
                        block.photos.length < 1 &&
                        block.type !== blockTypes.VIDEO &&
                        failedPhotos.length < 1 &&
                        !(
                            block.type === blockTypes.AUDIO &&
                            block.audioTrack?.processingFailure
                        ) &&
                        !alternativePlaceholderUrl
                    }
                >
                    <div className={styles.blockIndex}>
                        <span
                            className="icon has-text-warning is-medium"
                            title={i18n.generic.noImageSelectedWarning}
                        >
                            <i className="fas fa-2x fa-exclamation-circle"></i>
                        </span>
                    </div>
                </ShowIf>
                <ShowIf condition={failedPhotos.length > 0}>
                    <div className={styles.blockIndex}>
                        <span
                            className="icon has-text-danger is-medium"
                            onClick={(event) => {
                                event.stopPropagation();
                                showBlockPhotoProcessingFailureModal(block.id);
                            }}
                            title={failedPhotoTitle}
                        >
                            <i className="fas fa-2x fa-times-circle"></i>
                        </span>
                    </div>
                </ShowIf>
                <ShowIf
                    condition={
                        block.type === blockTypes.AUDIO &&
                        block.audioTrack?.processingFailure
                    }
                >
                    <div className={styles.blockIndex}>
                        <span
                            className="icon has-text-info is-medium"
                            title={i18n.generic.processingFailureMessage.replace(
                                "<FILENAME>",
                                get(block, ["audioTrack", "originalFileName"])
                            )}
                        >
                            <i className="fas fa-2x fa-exclamation-circle"></i>
                        </span>
                    </div>
                </ShowIf>
            </div>
            <div
                onClick={() => onWalkInOrOutBlockMediaPlaceholderClick(block)}
                className={`
          ${styles.extraHeader}
          ${hasAssignedAsset(block) ? styles.isDisabled : ""}
          ${hasAssignedAsset(block) ? styles.isPink : ""}
          ${!shouldShowExtraHeader(block) ? styles.isEmpty : ""}`}
            >
                <ShowIf
                    condition={
                        !hasAssignedAsset(block) ||
                        block.type === blockTypes.SPEAKER
                    }
                >
                    <ExtraHeaderPlaceholder />
                </ShowIf>
                <ShowIf condition={block.audioTrack}>
                    <span>
                        {formatFilename(
                            get(
                                block,
                                ["audioTrack", "originalFileName"],
                                "Muziek"
                            )
                        )}
                    </span>
                </ShowIf>
                <ShowIf condition={block.video}>
                    <span>
                        {formatFilename(
                            get(block, ["video", "originalFileName"], "Video")
                        )}
                    </span>
                </ShowIf>
            </div>
            <div
                className={`${styles.header} ${getBlockHeaderClassName(
                    block,
                    styles
                )}`}
            >
                <span className={styles.title}>
                    {getBlockTitle(block)}
                    <ShowIf
                        condition={
                            block.manualImageControl &&
                            block.type === blockTypes.SPEAKER
                        }
                    >
                        <span className="icon">
                            <i
                                className="fas fa-magic"
                                title={i18n.manualImageControl.hoverText}
                            ></i>
                        </span>
                    </ShowIf>{" "}
                    ({formatMediaDuration(block.duration)} min)
                </span>
            </div>
            <div className={styles.body}>
                <DroppableBlock
                    block={block}
                    render={(snapshot, placeholder) => (
                        <div
                            className={`tile is-ancestor ${
                                styles.photoTileAncestor
                            } ${
                                snapshot.isDraggingOver
                                    ? styles.photoTileActive
                                    : ""
                            }`}
                        >
                            <ShowIf condition={snapshot.isDraggingOver}>
                                <div className={styles.selectIconContainer}>
                                    <span className="icon">
                                        <i className="fas fa-plus"></i>
                                    </span>
                                </div>
                            </ShowIf>
                            <ShowIf
                                condition={
                                    block.type !== blockTypes.VIDEO &&
                                    (block.isWalkInBlock ||
                                        block.isWalkOutBlock)
                                }
                            >
                                <MultiplePhotos
                                    block={block}
                                    alternativePlaceholderImage={
                                        alternativePlaceholderImage
                                    }
                                    showBlockPhotoUploadModal={
                                        showBlockPhotoUploadModal
                                    }
                                />
                            </ShowIf>
                            <ShowIf
                                condition={
                                    block.type === blockTypes.AUDIO &&
                                    !block.isWalkInBlock &&
                                    !block.isWalkOutBlock
                                }
                            >
                                <ShowIf
                                    condition={
                                        !get(block, [
                                            "audioTrack",
                                            "processingFailure",
                                        ])
                                    }
                                >
                                    <MultiplePhotos
                                        block={block}
                                        alternativePlaceholderImage={
                                            alternativePlaceholderImage
                                        }
                                        showBlockPhotoUploadModal={
                                            showBlockPhotoUploadModal
                                        }
                                    />
                                </ShowIf>
                                <ShowIf
                                    condition={get(block, [
                                        "audioTrack",
                                        "processingFailure",
                                    ])}
                                >
                                    <div className="tile is-12 is-child">
                                        <div
                                            className={`${styles.videoObject} ${styles.isSingle}`}
                                        >
                                            <span
                                                className="icon is-right has-text-danger is-large"
                                                title={i18n.generic.processingFailureMessage.replace(
                                                    "<FILENAME>",
                                                    get(block, [
                                                        "audioTrack",
                                                        "originalFileName",
                                                    ])
                                                )}
                                            >
                                                <i className="fas fa-times-circle" />
                                            </span>
                                        </div>
                                    </div>
                                </ShowIf>
                            </ShowIf>
                            <ShowIf
                                condition={
                                    block.type === blockTypes.SPEAKER &&
                                    !block.isWalkInBlock &&
                                    !block.isWalkOutBlock
                                }
                            >
                                <ShowIf
                                    condition={
                                        speechPhotoLimit === 1 &&
                                        block.imageIds.length <= 1
                                    }
                                >
                                    <SinglePhoto
                                        block={block}
                                        alternativePlaceholderImage={
                                            alternativePlaceholderImage
                                        }
                                        showBlockPhotoUploadModal={
                                            showBlockPhotoUploadModal
                                        }
                                        allowPhotoUpload={allowPhotoUpload}
                                    />
                                </ShowIf>
                                <ShowIf
                                    condition={
                                        (speechPhotoLimit === 2 &&
                                            block.imageIds.length <= 2) ||
                                        (speechPhotoLimit === 1 &&
                                            block.imageIds.length === 2)
                                    }
                                >
                                    <DoublePhoto
                                        block={block}
                                        alternativePlaceholderImage={
                                            alternativePlaceholderImage
                                        }
                                        showBlockPhotoUploadModal={
                                            showBlockPhotoUploadModal
                                        }
                                        allowPhotoUpload={allowPhotoUpload}
                                    />
                                </ShowIf>
                                <ShowIf
                                    condition={
                                        (speechPhotoLimit === 1 ||
                                            speechPhotoLimit === 2) &&
                                        block.imageIds.length > 2
                                    }
                                >
                                    <MultiplePhotos
                                        block={block}
                                        alternativePlaceholderImage={
                                            alternativePlaceholderImage
                                        }
                                        hideFourthPhoto={
                                            block.imageIds.length === 3
                                        }
                                        showBlockPhotoUploadModal={
                                            showBlockPhotoUploadModal
                                        }
                                        allowPhotoUpload={allowPhotoUpload}
                                    />
                                </ShowIf>
                                <ShowIf condition={speechPhotoLimit === 3}>
                                    <MultiplePhotos
                                        block={block}
                                        alternativePlaceholderImage={
                                            alternativePlaceholderImage
                                        }
                                        hideFourthPhoto={
                                            block.imageIds.length <= 3
                                        }
                                        showBlockPhotoUploadModal={
                                            showBlockPhotoUploadModal
                                        }
                                        allowPhotoUpload={allowPhotoUpload}
                                    />
                                </ShowIf>
                                <ShowIf condition={speechPhotoLimit > 3}>
                                    <MultiplePhotos
                                        block={block}
                                        alternativePlaceholderImage={
                                            alternativePlaceholderImage
                                        }
                                        showBlockPhotoUploadModal={
                                            showBlockPhotoUploadModal
                                        }
                                        allowPhotoUpload={allowPhotoUpload}
                                    />
                                </ShowIf>
                            </ShowIf>
                            <ShowIf
                                condition={
                                    block.type === blockTypes.SLIDESHOW &&
                                    !block.isWalkInBlock &&
                                    !block.isWalkOutBlock
                                }
                            >
                                <MultiplePhotos
                                    block={block}
                                    alternativePlaceholderImage={
                                        alternativePlaceholderImage
                                    }
                                    showBlockPhotoUploadModal={
                                        showBlockPhotoUploadModal
                                    }
                                    allowPhotoUpload={allowPhotoUpload}
                                />
                            </ShowIf>
                            <ShowIf condition={block.type === blockTypes.VIDEO}>
                                <SingleVideo block={block} />
                            </ShowIf>
                            {placeholder}
                        </div>
                    )}
                ></DroppableBlock>
                <div
                    onClick={() => {
                        if (failedPhotos.length > 0) {
                            showBlockPhotoProcessingFailureModal(block.id);
                            return;
                        }

                        onEditButtonClick(block);
                    }}
                    className={`${styles.editButton} ${
                        block.photos.length > 4 ? styles.hasMore : ""
                    } `}
                >
                    <ShowIf condition={block.photos.length > 4}>
                        <span className={styles.editButtonLabel}>
                            +{block.photos.length - 4}
                        </span>
                    </ShowIf>
                    <span className="icon is-small">
                        <i className="fas fa-pen"></i>
                    </span>
                </div>
            </div>
            <ShowIf
                condition={
                    (room.manualImageControlsEnabled &&
                        block.type === blockTypes.SPEAKER) ||
                    (block.manualImageControl &&
                        block.type === blockTypes.SPEAKER)
                }
            >
                <div
                    onClick={() => onChangeImageControlClick(block)}
                    className={`button ${styles.photoControls}`}
                >
                    <div className={`${styles.controlOption}`}>
                        <div className={`${styles.iconSpace}`}>
                            <ShowIf condition={!block.manualImageControl}>
                                <span>
                                    <i
                                        className={`fas fa-check-circle ${styles.iconSpacing}`}
                                    ></i>
                                </span>
                            </ShowIf>
                        </div>
                        <span
                            className={
                                !block.manualImageControl
                                    ? styles.selectedOption
                                    : styles.unselectedOption
                            }
                        >
                            {i18n.imageControlModal.automatic}
                        </span>
                    </div>
                    <div className={`${styles.controlOption}`}>
                        <div className={`${styles.iconSpace}`}>
                            <ShowIf condition={block.manualImageControl}>
                                <span>
                                    <i
                                        className={`fas fa-check-circle ${styles.iconSpacing}`}
                                    ></i>
                                </span>
                            </ShowIf>
                        </div>
                        <span
                            className={
                                block.manualImageControl
                                    ? styles.selectedOption
                                    : styles.unselectedOption
                            }
                        >
                            {i18n.imageControlModal.manual}
                        </span>
                    </div>
                </div>
            </ShowIf>
        </div>
    );
};

export default BlockView;
